import { useState } from 'react';

import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { IconIntegration } from '@components/icons/integration';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IntegrationPollingType } from '@components/integrations/integrations-types';
import { Modal, useModal } from '@components/modal';
import { forceHideModal } from '@components/modal/modal';
import { RadioGroup } from '@components/radio-group';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';

import { routePaths } from 'ui/authenticated/navigation';

import { StyledScrollDiv } from './polling-endpoint-visibility';

export const useCreateCustomURLModal = () => useModal(CreateCustomURLModal);

export const CreateCustomURLModal = ({
  integrations,
  setChosenIntegration,
}: {
  integrations: IntegrationPollingType[];
  setChosenIntegration: (value: IntegrationPollingType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('');
  const hasIntegrations = integrations.length > 0;

  const handleAddCustomEndpoint = () => {
    if (selectedIntegrationId) {
      const integration = integrations.find(
        (integration) => integration.shortId === selectedIntegrationId
      );
      if (integration) {
        setChosenIntegration(integration);
      }
      forceHideModal();
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <Text text={t('selectIntegration')} font="regular14" color="textSecondary" />
          {hasIntegrations ? (
            <StyledScrollDiv>
              <RadioGroup
                label={t('selectIntegration')}
                hideLabel
                onChange={(value: string) => setSelectedIntegrationId(value)}
              >
                {integrations.map((integration) => (
                  <RadioGroup.Option
                    key={integration.shortId}
                    value={integration.shortId}
                    label={integration.name}
                    icon={
                      integration.thumbnail ? (
                        <SquareImage src={integration.thumbnail} />
                      ) : (
                        <IconIntegration color="elementPurpleInvert" />
                      )
                    }
                    textStyles={lineClamp('-webkit-inline-box', 1, true)}
                  />
                ))}
              </RadioGroup>
            </StyledScrollDiv>
          ) : (
            <EmptyState
              icon={<IconIntegration size={40} />}
              variant="small"
              header={t('emptyHeader')}
              content={t('emptyContent')}
              action={
                <Stack space={2}>
                  <Button
                    variant="primary"
                    text={t('integrationsPage')}
                    iconRight={<IconOpenNewTab size={16} color="buttonIconNegative" />}
                    size="sm"
                    href={routePaths.integrations}
                    target="_blank"
                  />
                </Stack>
              }
              skipMargin
            />
          )}
        </Stack>
      </Modal.Content>
      {hasIntegrations && (
        <Modal.Footer>
          <Stack space={2} orientation="horizontal" justify="flex-end">
            <Button variant="secondary" onClick={forceHideModal} text={t('core.cancel')} />
            <Button
              variant="primary-blue"
              onClick={handleAddCustomEndpoint}
              text={t('addCustom')}
              isDisabled={!selectedIntegrationId}
            />
          </Stack>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Create custom URL endpoint',
    selectIntegration:
      'Select an integration to use for authentication when accessing your custom URL endpoint',
    addCustom: 'Add custom endpoint',
    emptyHeader: 'No integrations',
    emptyContent:
      'You do not have any integrations, add and manage your integrations from the integrations page',
    integrationsPage: 'Integrations page',
  },
};
